import {
  PrinciplesConfigurationForm,
  PrinciplesConfigurationFormValues,
} from 'src/screens/administration/configuration-management/principles-configuration/principles/edit/PrinciplesConfigurationForm'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import { useLocation, useParams } from 'react-router'
import { OperationResult, useClient } from 'urql'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { Dispatch, useCallback, useEffect, useRef, useState } from 'react'
import { ROUTES } from 'src/routing/routes'
import {
  Mutation_Root,
  Principle_Config_Set_Input,
  Query_Root,
  Subprinciple_Config_Set_Input,
} from 'src/@types/graphql'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import {
  queryPrincipleConfigById,
  updatePrincipleConfig,
  updatePrincipleConfigAndRelatedSubprinciplesByPrincipleId,
} from 'src/screens/administration/configuration-management/principles-configuration/principles/principlesConfigQueries'

type OriginType = 'SAVE' | 'SAVE_AND_BACK'

type UpdatePrincipleConfigResponse =
  | OperationResult<{ update_principle_config: Mutation_Root['update_principle_config'] }>
  | OperationResult<{
      update_principle_config: Mutation_Root['update_principle_config']
      update_subprinciple_config: Mutation_Root['update_subprinciple_config']
    }>

const submitPrinciplesConfigForm = () => {
  document.getElementById('principles-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const PrinciplesConfigurationPrinciplesEditPage = () => {
  const { principleId } = useParams()
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const { state } = useLocation()

  const [initialValues, setInitialValues] = useState<PrinciplesConfigurationFormValues | null>(null)
  const originRef = useRef<OriginType>('SAVE')

  const onBackHandler = useCallback(() => {
    const navigatedFromSortListView =
      state?.navigatedFromSortListView !== undefined &&
      state?.navigatedFromSortListView !== null &&
      state.navigatedFromSortListView === true

    if (navigatedFromSortListView) {
      navigate(ROUTES.PrinciplesConfigurationIndex.nested.PrinciplesConfigurationPreview.params({}))
    } else {
      navigate(ROUTES.PrinciplesConfigurationIndex.nested.PrinciplesConfigurationPrinciples.params({}))
    }
  }, [navigate, state])

  const onSave = async () => {
    originRef.current = 'SAVE'
    submitPrinciplesConfigForm()
  }

  const onSaveAndBack = async () => {
    originRef.current = 'SAVE_AND_BACK'
    submitPrinciplesConfigForm()
  }

  const fetchPrincipleConfigToEdit = useCallback(async () => {
    if (!principleId) {
      throw new Error()
    }

    const { data, error } = await urqlClient
      .query<{
        principle_config: Query_Root['principle_config']
      }>(queryPrincipleConfigById, { principleId })
      .toPromise()

    if (error || !data?.principle_config?.[0]) {
      throw new Error('Resource not found')
    }

    const fetchedPrincipleConfig = data.principle_config[0]
    const editingPrincipleConfig: PrinciplesConfigurationFormValues = {
      enName: fetchedPrincipleConfig.names['en'],
      deName: fetchedPrincipleConfig.names['de'],
      frName: fetchedPrincipleConfig.names['fr'],
      itName: fetchedPrincipleConfig.names['it'],
      status: fetchedPrincipleConfig.active ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE,
      enHelpInstructions: fetchedPrincipleConfig.help_and_instructions['en'],
      deHelpInstructions: fetchedPrincipleConfig.help_and_instructions['de'],
      frHelpInstructions: fetchedPrincipleConfig.help_and_instructions['fr'],
      itHelpInstructions: fetchedPrincipleConfig.help_and_instructions['it'],
      helpAndInstructionsKey: fetchedPrincipleConfig.help_and_instruction_key,
    }

    return { editingPrincipleConfig }
  }, [principleId, urqlClient])

  const updatePrincipleConfigToActive = useCallback(
    async (principleId: number, principleSetInput: Principle_Config_Set_Input) => {
      return await urqlClient
        .mutation<{
          update_principle_config: Mutation_Root['update_principle_config']
        }>(updatePrincipleConfig, {
          principleId: principleId,
          principleSetInput: principleSetInput,
        })
        .toPromise()
    },
    [urqlClient],
  )

  const updatePrincipleConfigToInactive = useCallback(
    async (principleId: number, principleSetInput: Principle_Config_Set_Input) => {
      const subprincipleSetInput: Subprinciple_Config_Set_Input = {
        active: false,
      }

      return await urqlClient
        .mutation<{
          update_principle_config: Mutation_Root['update_principle_config']
          update_subprinciple_config: Mutation_Root['update_subprinciple_config']
        }>(updatePrincipleConfigAndRelatedSubprinciplesByPrincipleId, {
          principleId,
          principleSetInput,
          subprincipleSetInput,
        })
        .toPromise()
    },
    [urqlClient],
  )

  const handleSubmitLocal = useCallback(
    async (values: PrinciplesConfigurationFormValues, setFormDirty: Dispatch<boolean>) => {
      const isActiveSelected = values.status === CONFIGURATION_STATUS.ACTIVE

      const principleSetInput: Principle_Config_Set_Input = {
        names: {
          de: values.deName ?? '',
          en: values.enName ?? '',
          fr: values.frName ?? '',
          it: values.itName ?? '',
        },
        active: values.status === CONFIGURATION_STATUS.ACTIVE,
        help_and_instructions: {
          de: values.deHelpInstructions ? HtmlSanitizer.sanitize(values.deHelpInstructions) : '',
          en: values.enHelpInstructions ? HtmlSanitizer.sanitize(values.enHelpInstructions) : '',
          fr: values.frHelpInstructions ? HtmlSanitizer.sanitize(values.frHelpInstructions) : '',
          it: values.itHelpInstructions ? HtmlSanitizer.sanitize(values.itHelpInstructions) : '',
        },
      }

      let updatePrincipleResponse: UpdatePrincipleConfigResponse
      const principleIdParsed = parseInt(principleId as string)
      if (isActiveSelected) {
        updatePrincipleResponse = await updatePrincipleConfigToActive(principleIdParsed, principleSetInput)
      } else {
        updatePrincipleResponse = await updatePrincipleConfigToInactive(principleIdParsed, principleSetInput)
      }

      const { data, error } = updatePrincipleResponse
      if (!error && data?.update_principle_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setInitialValues(values)
        setFormDirty(false)
        if (originRef.current === 'SAVE_AND_BACK') {
          onBackHandler()
        }
      } else {
        notificationService.operationFailed()
      }
    },
    [notificationService, onBackHandler, principleId, updatePrincipleConfigToActive, updatePrincipleConfigToInactive],
  )

  useEffect(() => {
    fetchPrincipleConfigToEdit()
      .then((res) => {
        setInitialValues(res.editingPrincipleConfig)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [fetchPrincipleConfigToEdit, notificationService])

  return (
    <ScreenLayout
      title={getMessage('label.configuration.principles.edit')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
          <SaveButton origin="header" onClick={onSave} />
        </>
      }
    >
      <PageLayout>
        <>
          {initialValues && (
            <PrinciplesConfigurationForm mode="EDIT" onSubmit={handleSubmitLocal} initialValues={initialValues} />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
